import { User as Auth0User } from '@auth0/auth0-spa-js';
import { useAuth0 } from '@auth0/auth0-react';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { PricingPlan } from '../models/pricing-plan';
import { User } from '../models/user';
import axios from 'axios';

export interface Organization {
  id: string;
  name: string;
  emails: string[];
  date_created: string;
  me: User;
  data_export_allowed: boolean
  credits: number
  acled_countries: string[]
  has_all_countries: boolean
  has_sdk_license?: boolean
  sdk_domain_whitelist?: string[]
}

interface OrganizationContextProps {
  accountLinked: boolean
  loginUser: (userInfo: Auth0User) => Promise<{ new_user: boolean, organization: Organization }>;
  organization: Organization | null;
  setOrganization: React.Dispatch<React.SetStateAction<Organization | null>>;
  loading: boolean;
}

interface CurrentPlanSetterContextProps {
  currentPlan: PricingPlan | null;
  setCurrentPlan: (plan: PricingPlan | null) => void;
}

interface OrganizationProviderProps {
  children: React.ReactNode;
}

const OrganizationContext = createContext<OrganizationContextProps | undefined>(undefined);
export const CurrentPlanSetterContext = createContext<CurrentPlanSetterContextProps | undefined>(undefined);

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [accountLinked, setAccountLinked] = useState<boolean>(false);
  const [currentPlan, setCurrentPlan] = useState<PricingPlan | null>(null);
  const { user, getAccessTokenSilently } = useAuth0();

  const loginUser = useCallback(async (userInfo: Auth0User) => {
    setLoading(true);
    if (!user) return;
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.post(
        process.env.REACT_APP_API_BASE_URL + '/auth/login',
        { email: user.email, user_info: userInfo },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setOrganization(data.organization);
      setAccountLinked(data.account_linked ?? false);
      setCurrentPlan(data.organization?.current_plan || null);
      return data;
    } finally {
      setLoading(false);
    }
  }, [user, getAccessTokenSilently]);

  return (
    <OrganizationContext.Provider value={{ organization, loginUser, setOrganization, loading, accountLinked }}>
      <CurrentPlanSetterContext.Provider value={{ currentPlan, setCurrentPlan }}>
        {children}
      </CurrentPlanSetterContext.Provider>
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within a OrganizationProvider');
  }
  return context;
};

export const useCurrentPlan = () => {
  const context = useContext(CurrentPlanSetterContext);
  if (context === undefined) {
    throw new Error('useCurrentPlanSetter must be used within a CurrentPlanSetterProvider');
  }
  return context;
};
